import { lazy, ComponentType } from 'react';
import { APP_REGISTRY } from '@/config/navigation';

// Component mapping for all pages
const COMPONENT_MAP: Record<string, ComponentType<any>> = {
  'home': lazy(() => import('@/pages/Dashboard')),
  'feed': lazy(() => import('@/pages/Feed')),
  'my-ai-twin': lazy(() => import('@/pages/MyAITwin')),
  'content-planner': lazy(() => import('@/pages/ContentPlanner')),
  'my-vault': lazy(() => import('@/pages/MyVault')),
  'meme-vault': lazy(() => import('@/pages/MemeVault')),
  
  'social': lazy(() => import('@/pages/SocialFeed')),
  'video': lazy(() => import('@/pages/VideoApps')),
  'rowdy-rule': lazy(() => import('@/pages/RowdyRuleGame')),
  'post-igniter': lazy(() => import('@/pages/PostIgniter')),
  'post-igniter-v2': lazy(() => import('@/pages/PostIgniterV2')),
  'nano-banana': lazy(() => import('@/pages/NanoBanana')),
  'viral-loop': lazy(() => import('@/pages/ViralLoop')),
  'viral-loop-v2': lazy(() => import('@/pages/ViralLoopV2')),
  'viral-story-alchemist': lazy(() => import('@/pages/ViralStoryAlchemist')),
  'viral-content-studio': lazy(() => import('@/pages/ViralContentStudio')),
  'psycho-viral-creator': lazy(() => import('@/pages/PsychoViralCreator')),
  'create-content': lazy(() => import('@/pages/CreateContent')),
  'ai-chat': lazy(() => import('@/pages/AIChat')),
  'explore': lazy(() => import('@/pages/Explore')),
  'social-pulse': lazy(() => import('@/pages/SocialPulse')),
  'content-architect': lazy(() => import('@/pages/ContentArchitect')),
  'meme-generator': lazy(() => import('@/pages/MemeGenerator')),
  'metamasters-meme-studio': lazy(() => import('@/pages/MetaMastersMemeStudio')),
  'meme-remix': lazy(() => import('@/pages/MemeRemix')),
  'ai-photo-editor': lazy(() => import('@/pages/AIPhotoEditor')),
  'twin-image-generator': lazy(() => import('@/pages/TwinImageGenerator')),
  'faceless-video-generator': lazy(() => import('@/pages/FacelessVideoGenerator')),
  'ai-video-generator': lazy(() => import('@/pages/AIVideoGenerator')),
  'viral-ai-video': lazy(() => import('@/pages/AIVideoGenerator')),
  'viral-video-generator': lazy(() => import('@/pages/ViralVideoGenerator')),
  'luma-video-generator': lazy(() => import('@/pages/LumaVideoGenerator')),
  'god-mode': lazy(() => import('@/pages/GodMode')),
  'auto-strategist': lazy(() => import('@/pages/AutoStrategist')),
  'ai-agent-builder': lazy(() => import('@/pages/AIAgentBuilder')),
  'veo-ai': lazy(() => import('@/pages/VeoAI')),
  'sora-ai': lazy(() => import('@/pages/SoraAI')),
  'imagen-grok': lazy(() => import('@/pages/ImagenGrok')),
  'sprite-animator': lazy(() => import('@/pages/SpriteAnimator')),
  'topic-generator': lazy(() => import('@/pages/TopicGenerator')),
  'virality-score': lazy(() => import('@/pages/ViralityScore')),
  'user-hq': lazy(() => import('@/pages/UserHQ')),
  'surveillance-hq': lazy(() => import('@/pages/SurveillanceHQ')),
  'achievements': lazy(() => import('@/pages/Achievements')),
  'apps': lazy(() => import('@/pages/Apps')),
  'usage-dashboard': lazy(() => import('@/pages/UsageDashboard')),
  'usage-history': lazy(() => import('@/pages/UsageHistory')),
  'profile': lazy(() => import('@/pages/Profile')),
  'settings': lazy(() => import('@/pages/Settings')),
  'support': lazy(() => import('@/pages/Support')),
  'notifications': lazy(() => import('@/pages/Notifications')),
  'studio-apps': lazy(() => import('@/pages/StudioApps')),
  'textforge-apps': lazy(() => import('@/pages/TextForgeApps')),
  'metamaster-apps': lazy(() => import('@/pages/MetaMasterApps')),
  'reset-password': lazy(() => import('@/pages/ResetPassword')),
  'graphics': lazy(() => import('@/pages/GraphicsApps')),
  'memes': lazy(() => import('@/pages/MemesApps')),
  
  'script-creator': lazy(() => import('@/pages/ScriptCreator')),
  'nano-banana-simple': lazy(() => import('@/pages/NanoBananaSimple')),
  'content-studio': lazy(() => import('@/pages/ContentStudio')),
  'help': lazy(() => import('@/pages/Help')),
  'navigation-demo': lazy(() => import('@/pages/NavigationDemo')),
  'mac-test': lazy(() => import('@/pages/Test')),
  'audience-intelligence': lazy(() => import('@/pages/AudienceIntelligence')),
  'audience-import': lazy(() => import('@/pages/AudienceImport')),
  'audience-segmentation': lazy(() => import('@/pages/AudienceSegmentation')),
  'audience-dashboard': lazy(() => import('@/pages/AudienceDashboard')),
  'optimization-hub': lazy(() => import('@/pages/OptimizationHub')),
  'app-manager': lazy(() => import('@/pages/AppManager')),
  'reels-lab': lazy(() => import('@/pages/reels-lab/ReelsLabHome')),
  'reels-lab-create': lazy(() => import('@/pages/reels-lab/ReelsCreate')),
  'reels-lab-library': lazy(() => import('@/pages/reels-lab/ReelsLibrary')),
  'reels-lab-editor': lazy(() => import('@/pages/ReelsLabEditor')),
  'app-store': lazy(() => import('@/pages/AppStore')),
  'marketplace': lazy(() => import('@/pages/Marketplace')),
  'about': lazy(() => import('@/pages/AboutCommandOS')),
  'social-post-generator': lazy(() => import('@/pages/SocialPostGenerator')),
};

export interface RouteConfig {
  path: string;
  component: ComponentType<any>;
  requiresAuth: boolean;
  requiresAdmin?: boolean;
  requiresPlan?: string;
}

// Auto-generate routes from APP_REGISTRY
export const appRoutes: RouteConfig[] = APP_REGISTRY
  .filter(app => app.route !== null && COMPONENT_MAP[app.id])
  .map(app => ({
    path: app.route as string,
    component: COMPONENT_MAP[app.id],
    requiresAuth: app.requiresAuth,
    requiresAdmin: app.requiresAdmin,
    requiresPlan: app.requiresPlan,
    fullScreen: app.fullScreen,
  }));

// Special routes not in registry
export const specialRoutes: RouteConfig[] = [
  {
    path: '/',
    component: lazy(() => import('@/pages/Index')),
    requiresAuth: false,
  },
  {
    path: '/auth',
    component: lazy(() => import('@/pages/Auth')),
    requiresAuth: false,
  },
  {
    path: '/login',
    component: lazy(() => import('@/pages/Login')),
    requiresAuth: false,
  },
  {
    path: '/signup',
    component: lazy(() => import('@/pages/Signup')),
    requiresAuth: false,
  },
  {
    path: '/pricing',
    component: lazy(() => import('@/pages/Pricing')),
    requiresAuth: false,
  },
  {
    path: '/compare',
    component: lazy(() => import('@/pages/CompareCommandOS')),
    requiresAuth: false,
  },
  {
    path: '/mfa/enroll',
    component: lazy(() => import('@/pages/MFAEnroll')),
    requiresAuth: true,
  },
  {
    path: '/mfa/challenge',
    component: lazy(() => import('@/pages/MFAChallenge')),
    requiresAuth: false,
  },
  {
    path: '/features',
    component: lazy(() => import('@/pages/Features')),
    requiresAuth: true,
  },
  {
    path: '/features/:featureId',
    component: lazy(() => import('@/pages/FeatureDetail')),
    requiresAuth: true,
  },
  {
    path: '/admin-panel/features',
    component: lazy(() => import('@/pages/admin/FeaturesManagement')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/admin/devops-monitor',
    component: lazy(() => import('@/pages/AdminDevOpsMonitor')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/logout',
    component: lazy(() => import('@/pages/Logout')),
    requiresAuth: false,
  },
  {
    path: '/my-ai-twin/onboarding',
    component: lazy(() => import('@/pages/MyAITwinOnboarding')),
    requiresAuth: true,
  },
  {
    path: '/my-ai-twin/train',
    component: lazy(() => import('@/pages/MyAITwinTraining')),
    requiresAuth: true,
  },
  {
    path: '/my-ai-twin/training',
    component: lazy(() => import('@/pages/AITwinTraining')),
    requiresAuth: true,
  },
  {
    path: '/my-ai-twin/preview',
    component: lazy(() => import('@/pages/AITwinPreview')),
    requiresAuth: true,
  },
  {
    path: '/my-ai-twin/pro-welcome',
    component: lazy(() => import('@/pages/MyAITwinProWelcome')),
    requiresAuth: true,
  },
  {
    path: '/settings/sessions',
    component: lazy(() => import('@/pages/ActiveSessions')),
    requiresAuth: true,
  },
  {
    path: '/ai-chat/:conversationId',
    component: lazy(() => import('@/pages/AIChat')),
    requiresAuth: true,
  },
  {
    path: '/social/following',
    component: lazy(() => import('@/pages/SocialFeedFollowing')),
    requiresAuth: true,
  },
  {
    path: '/profile/:userId',
    component: lazy(() => import('@/pages/PublicProfile')),
    requiresAuth: true,
  },
  {
    path: '/support/ticket/:ticketId',
    component: lazy(() => import('@/pages/TicketDetail')),
    requiresAuth: true,
  },
  {
    path: '/admin',
    component: lazy(() => import('@/pages/AdminPanel')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/admin-panel',
    component: lazy(() => import('@/pages/AdminPanel')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/admin/storage',
    component: lazy(() => import('@/pages/AdminStorageManagement')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/usage-dashboard',
    component: lazy(() => import('@/pages/UsageDashboard')),
    requiresAuth: true,
  },
  {
    path: '/usage-history',
    component: lazy(() => import('@/pages/UsageHistory')),
    requiresAuth: true,
  },
  {
    path: '/admin-panel/plans',
    component: lazy(() => import('@/pages/PlanSettings')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/studio',
    component: lazy(() => import('@/pages/StudioApps')),
    requiresAuth: true,
  },
  {
    path: '/textforge',
    component: lazy(() => import('@/pages/TextForgeApps')),
    requiresAuth: true,
  },
  {
    path: '/metamaster',
    component: lazy(() => import('@/pages/MetaMasterApps')),
    requiresAuth: true,
  },
  {
    path: '/graphics',
    component: lazy(() => import('@/pages/GraphicsApps')),
    requiresAuth: true,
  },
  {
    path: '/memes',
    component: lazy(() => import('@/pages/MemesApps')),
    requiresAuth: true,
  },
  {
    path: '/ubiquity',
    component: lazy(() => import('@/pages/Ubiquity')),
    requiresAuth: true,
  },
  {
    path: '/reset-password',
    component: lazy(() => import('@/pages/ResetPassword')),
    requiresAuth: false,
  },
  {
    path: '/share',
    component: lazy(() => import('@/pages/ShareToCommandOS')),
    requiresAuth: false,
  },
  {
    path: '/all-apps',
    component: lazy(() => import('@/pages/AllApps')),
    requiresAuth: true,
  },
  {
    path: '/reels-lab/create',
    component: COMPONENT_MAP['reels-lab-create'],
    requiresAuth: true,
  },
  {
    path: '/reels-lab/library',
    component: COMPONENT_MAP['reels-lab-library'],
    requiresAuth: true,
  },
  {
    path: '/reels-lab/editor',
    component: COMPONENT_MAP['reels-lab-editor'],
    requiresAuth: true,
  },
  {
    path: '/reels-lab',
    component: COMPONENT_MAP['reels-lab'],
    requiresAuth: true,
  },
  {
    path: '/viral-library',
    component: lazy(() => import('@/pages/ViralLibrary')),
    requiresAuth: true,
  },
  {
    path: '/viral-admin',
    component: lazy(() => import('@/pages/ViralAdmin')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/help',
    component: lazy(() => import('@/pages/Help')),
    requiresAuth: true,
  },
  {
    path: '/autonomous-learning',
    component: lazy(() => import('@/pages/AutonomousLearning')),
    requiresAuth: true,
  },
  {
    path: '/privacy',
    component: lazy(() => import('@/pages/PrivacyPolicy')),
    requiresAuth: false,
  },
  {
    path: '/terms',
    component: lazy(() => import('@/pages/TermsOfService')),
    requiresAuth: false,
  },
  {
    path: '/compliance-dashboard',
    component: lazy(() => import('@/pages/ComplianceDashboard')),
    requiresAuth: true,
  },
  {
    path: '/compliance-admin',
    component: lazy(() => import('@/pages/ComplianceAdmin')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/referral-dashboard',
    component: lazy(() => import('@/pages/ReferralDashboard')),
    requiresAuth: true,
  },
  {
    path: '/ticket-lookup',
    component: lazy(() => import('@/pages/TicketLookup')),
    requiresAuth: false,
  },
  {
    path: '/updates',
    component: lazy(() => import('@/pages/Updates')),
    requiresAuth: true,
  },
  {
    path: '/admin/usage-test',
    component: lazy(() => import('@/pages/admin/UsageTestPage')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/admin/app-manager',
    component: lazy(() => import('@/pages/AppManager')),
    requiresAuth: true,
    requiresAdmin: true,
  },
  {
    path: '/audience-intelligence',
    component: lazy(() => import('@/pages/AudienceIntelligence')),
    requiresAuth: true,
  },
  {
    path: '/optimization-hub',
    component: lazy(() => import('@/pages/OptimizationHub')),
    requiresAuth: true,
  },
  {
    path: '/audience-intelligence/import',
    component: lazy(() => import('@/pages/AudienceImport')),
    requiresAuth: true,
  },
  {
    path: '/app-store',
    component: lazy(() => import('@/pages/AppStore')),
    requiresAuth: true,
  },
  {
    path: '/about',
    component: lazy(() => import('@/pages/AboutCommandOS')),
    requiresAuth: true,
  },
  {
    path: '/image-library',
    component: lazy(() => import('@/pages/ImageLibrary')),
    requiresAuth: true,
  },
  {
    path: '/virality-lab',
    component: lazy(() => import('@/pages/ViralityLab')),
    requiresAuth: true,
  },
  {
    path: '*',
    component: lazy(() => import('@/pages/NotFound')),
    requiresAuth: false,
  },
];

// Combine all routes
export const allRoutes = [...appRoutes, ...specialRoutes];
